import { useEffect, useState } from 'react';

export type SpotfireWebClientState = 'idle' | 'loading' | 'opened' | 'error';

// Classic (pre-OAuth) mashup API — cookie-session based, so it rides the same anonymous session
// through the relay as the plain web player. The modern js-api/oauth.js generation requires an
// OAuth client registration bound to the embedding origin, which doesn't fit the portal setup.
const SPOTFIRE_CLASSIC_API_VERSION = '7.14';

const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if ((window as any).spotfire?.webPlayer?.Application) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });

/**
 * Boots the Spotfire web client (classic mashup JS API) into the container div from the
 * server-composed embed fields: serverUrl -> web player root, analysisPath -> the analysis to
 * open, configurationBlock -> open parameters (the user's filterId row filter). Chrome
 * customization hides the player menus. On any failure the state becomes 'error' and the caller
 * shows an error message instead of the embed.
 */
export const useSpotfireWebClient = (
  serverUrl: string | undefined | null,
  analysisPath: string | undefined | null,
  configurationBlock: string,
  containerId: string,
  enabled: boolean
): SpotfireWebClientState => {
  const [state, setState] = useState<SpotfireWebClientState>('idle');

  useEffect(() => {
    if (!enabled || !serverUrl || !analysisPath) {
      setState('idle');
      return;
    }

    let cancelled = false;
    let app: any;
    setState('loading');

    (async () => {
      try {
        const server = new URL(serverUrl).origin;

        await loadScript(
          `${server}/spotfire/wp/GetJavaScriptApi.ashx?Version=${SPOTFIRE_CLASSIC_API_VERSION}`
        );
        if (cancelled) {
          return;
        }
        const webPlayer = (window as any).spotfire?.webPlayer;
        if (!webPlayer?.Application || !webPlayer?.Customization) {
          throw new Error('spotfire classic js-api did not initialize');
        }

        const customization = new webPlayer.Customization();
        customization.showToolBar = false;
        customization.showStatusBar = false;
        customization.showPageNavigation = true;

        app = new webPlayer.Application(
          `${server}/spotfire/wp/`,
          customization,
          analysisPath,
          configurationBlock,
          true,
          webPlayer.apiVersion || SPOTFIRE_CLASSIC_API_VERSION
        );
        app.onError((errorCode: any, description: any) => {
          console.warn('Spotfire web client error', errorCode, description);
          if (!cancelled) {
            setState('error');
          }
        });
        app.onOpened(() => {
          if (!cancelled) {
            setState('opened');
          }
        });
        app.openDocument(containerId);
      } catch (e) {
        console.warn('Spotfire web client failed to boot', e);
        if (!cancelled) {
          setState('error');
        }
      }
    })();

    return () => {
      cancelled = true;
      try {
        app?.close?.();
      } catch (e) {
        // best effort on unmount
      }
    };
  }, [serverUrl, analysisPath, configurationBlock, containerId, enabled]);

  return state;
};
